import BN from 'bn.js';
import { common, InvalidFormatError } from './common';
import { utils } from './utils';
class Op {
    constructor(options) {
        this.fn = options.fn;
        this.length = options.length;
        this.value = options.value;
    }
    get next() {
        return this.mutableNext;
    }
    setNext(nextOp) {
        if (this.mutableNext !== undefined) {
            throw new Error('next operation already set for this op.');
        }
        this.mutableNext = nextOp;
    }
}
const writeByte = (value, buffer, position) => {
    buffer[position] = value & 255;
};
const writeFromBuffer = (source, target, offset = 0) => {
    source.forEach((bit, index) => {
        target[index + offset] = bit & 255;
    });
};
const writeUInt160 = (value, buffer, position) => {
    const result = common.uInt160ToBuffer(value);
    if (result.length !== common.UINT160_BUFFER_BYTES) {
        throw new Error('is this working?');
    }
    writeFromBuffer(result, buffer, position);
};
const writeUInt256 = (value, buffer, position) => {
    const result = common.uInt256ToBuffer(value);
    if (result.length !== common.UINT256_BUFFER_BYTES) {
        throw new Error('is this working?');
    }
    writeFromBuffer(result, buffer, position);
};
const writeECPoint = (value, buffer, position) => {
    const result = common.ecPointToBuffer(value);
    if (result.length !== common.ECPOINT_BUFFER_BYTES) {
        throw new Error('is this working?');
    }
    writeFromBuffer(result, buffer, position);
};
export class BinaryWriter {
    constructor() {
        this.head = new Op({
            fn: () => {
            },
            length: 0,
            value: undefined,
        });
        this.mutableTail = this.head;
        this.mutableLength = 0;
    }
    get length() {
        return this.mutableLength;
    }
    toBuffer() {
        return this.finish();
    }
    writeBytes(value) {
        return this.push({
            fn: writeFromBuffer,
            length: value.length,
            value,
        });
    }
    writeUInt8(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeUInt8(val, pos),
            length: 1,
            value,
        });
    }
    writeInt16LE(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeInt16LE(val, pos),
            length: 2,
            value,
        });
    }
    writeUInt16LE(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeUInt16LE(val, pos),
            length: 2,
            value,
        });
    }
    writeUInt16BE(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeUInt16BE(val, pos),
            length: 2,
            value,
        });
    }
    writeInt32LE(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeInt32LE(val, pos),
            length: 4,
            value,
        });
    }
    writeUInt32LE(value) {
        return this.push({
            fn: (val, buffer, pos) => buffer.writeUInt32LE(val, pos),
            length: 4,
            value,
        });
    }
    writeInt64LE(value) {
        return this.push({
            fn: (val, buffer, pos) => {
                const source = val.toTwos(8 * 8).toArrayLike(Buffer, 'le', 8);
                writeFromBuffer(source, buffer, pos);
            },
            length: 8,
            value,
        });
    }
    writeUInt64LE(value) {
        return this.push({
            fn: (val, buffer, pos) => {
                const source = val.toArrayLike(Buffer, 'le', 8);
                writeFromBuffer(source, buffer, pos);
            },
            length: 8,
            value,
        });
    }
    writeBoolean(value) {
        return this.push({
            fn: writeByte,
            length: 1,
            value: value ? 1 : 0,
        });
    }
    writeUInt160(hash) {
        return this.push({
            fn: writeUInt160,
            length: common.UINT160_BUFFER_BYTES,
            value: hash,
        });
    }
    writeUInt256(hash) {
        return this.push({
            fn: writeUInt256,
            length: common.UINT256_BUFFER_BYTES,
            value: hash,
        });
    }
    writeFixed8(value) {
        return this.writeInt64LE(value);
    }
    writeFixedString(value, length) {
        if (value.length > length) {
            throw new InvalidFormatError('String too long');
        }
        const stringBuffer = Buffer.from(value, 'utf8');
        if (stringBuffer.length > length) {
            throw new InvalidFormatError('String buffer length too long');
        }
        return this.push({
            fn: (val, buffer, pos) => {
                writeFromBuffer(val, buffer, pos);
                const padLength = val.length - length;
                if (padLength > 0) {
                    writeFromBuffer(Buffer.alloc(padLength, 0), buffer, pos + val.length);
                }
            },
            length,
            value: stringBuffer,
        });
    }
    writeArray(values, write) {
        this.writeVarUIntLE(values.length);
        values.forEach(write);
        return this;
    }
    writeObject(value, write) {
        const entries = Object.entries(value);
        this.writeVarUIntLE(entries.length);
        entries.forEach(([key, val]) => {
            write(key, val);
        });
        return this;
    }
    writeVarBytesLE(value) {
        this.writeVarUIntLE(value.length);
        return this.push({
            fn: (val, buffer, pos) => {
                writeFromBuffer(val, buffer, pos);
            },
            length: value.length,
            value,
        });
    }
    writeVarUIntLE(valueIn) {
        const value = new BN(valueIn);
        if (value.lt(utils.ZERO)) {
            throw new InvalidFormatError('Expected value to be zero or positive');
        }
        if (value.lt(utils.FD)) {
            this.writeUInt8(value.toNumber());
        }
        else if (value.lte(utils.FFFF)) {
            this.writeUInt8(0xfd);
            this.writeUInt16LE(value.toNumber());
        }
        else if (value.lte(utils.FFFFFFFF)) {
            this.writeUInt8(0xfe);
            this.writeUInt32LE(value.toNumber());
        }
        else {
            this.writeUInt8(0xff);
            this.writeUInt64LE(value);
        }
        return this;
    }
    writeVarString(value, max) {
        let buffer = Buffer.from(value, 'utf8');
        if (max !== undefined) {
            buffer = buffer.slice(0, max);
        }
        return this.writeVarBytesLE(buffer);
    }
    writeECPoint(value) {
        if (common.ecPointIsInfinity(value)) {
            return this.push({
                fn: writeByte,
                length: 1,
                value: common.ECPOINT_INFINITY_BYTE,
            });
        }
        return this.push({
            fn: writeECPoint,
            length: common.ECPOINT_BUFFER_BYTES,
            value,
        });
    }
    finish() {
        const computedBuffer = Buffer.alloc(this.length);
        let head = this.head.next;
        let position = 0;
        while (head !== undefined) {
            head.fn(head.value, computedBuffer, position);
            position += head.length;
            head = head.next;
        }
        return computedBuffer;
    }
    push(op) {
        const newOp = new Op(op);
        this.mutableTail.setNext(newOp);
        this.mutableTail = newOp;
        this.mutableLength += op.length;
        return this;
    }
}

//# sourceMappingURL=BinaryWriter.js.map
